import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, Target, Trophy } from "lucide-react";
import { Shell } from "@/components/Shell";
import { InstallPrompt } from "@/components/InstallPrompt";
import { ContactLinks } from "@/components/ContactLinks";
import { useAuth } from "@/context/AuthContext";
import { api, fmtDuration, fmtSGT } from "@/lib/api";
import { useCountdown } from "@/hooks/useCountdown";

const STEPS = (days) => [
  { icon: CalendarDays, title: "3 puzzles a day", text: `Same 3 pattern & logic challenges for every player, every day for ${days} days.` },
  { icon: Target, title: "Start, solve, submit", text: "Press Start, answer all three, then Submit. Correct = 100, 300 or 600 points. Wrong = 0. One attempt." },
  { icon: Trophy, title: "Top the leaderboard", text: `Ranked by total points; your total solving time across ${days} days breaks ties. Top players win at the end of Day ${days}.` },
];

const ordinal = (n) => { const s = ["th", "st", "nd", "rd"], v = n % 100; return n + (s[(v - 20) % 10] || s[v] || s[0]); };

export default function Landing() {
  const { user } = useAuth();
  const [season, setSeason] = useState(null);
  useEffect(() => { api.get("/season").then(({ data }) => setSeason(data)).catch(() => {}); }, []);
  const untilStart = useCountdown(season?.status === "upcoming" ? season.start_at : null, season?.server_now);
  const days = season?.total_days ?? 7;
  const prizes = season?.prizes ?? [];

  return (
    <Shell width="wide">
      <div className="md:grid md:grid-cols-12 md:items-start md:gap-12">
      <section className="fade-up md:col-span-7 lg:col-span-6">
        {season && (
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-slate-800 px-3 py-1.5" data-testid="season-status-badge">
            <span className={`h-2 w-2 rounded-full ${season.status === "live" ? "bg-emerald-400 animate-pulse" : season.status === "upcoming" ? "bg-amber-400" : "bg-slate-500"}`} />
            <span className="eyebrow text-slate-300">
              {season.status === "live" && `Day ${season.day} of ${season.total_days} · Live`}
              {season.status === "upcoming" && `Opens ${fmtSGT(season.start_at)} SGT${untilStart != null ? ` · ${fmtDuration(untilStart)}` : ""}`}
              {season.status === "ended" && "Season ended"}
            </span>
          </div>
        )}
        <h1 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl" data-testid="landing-title">
          <span className="block">{days} days.</span><span className="block">{days * 3} puzzles.</span><span className="block text-blue-400">One leaderboard.</span>
        </h1>
        <p className="mt-5 max-w-md text-base leading-relaxed text-slate-400 md:text-lg">
          Singapore's free daily pattern & logic competition. Solve three official challenges a day and climb the season ranks.
        </p>
        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
          {user ? (
            <Link to="/play" className="btn-primary sm:min-w-[240px]" data-testid="start-challenge-button">Start today's challenge <ArrowRight size={18} /></Link>
          ) : (
            <>
              <Link to="/register" className="btn-primary sm:min-w-[220px]" data-testid="register-cta-button">Create free account <ArrowRight size={18} /></Link>
              <Link to="/login" className="btn-secondary" data-testid="login-cta-button">I already have an account</Link>
            </>
          )}
          <Link to="/leaderboard" className="py-2 text-center text-sm font-medium text-slate-400 transition-colors hover:text-white sm:px-3" data-testid="landing-leaderboard-link">View leaderboard</Link>
        </div>
        <div className="hidden md:block"><InstallPrompt /></div>
      </section>

      <section className="mt-12 space-y-3 md:col-span-5 md:mt-0 lg:col-span-6">
        <p className="eyebrow">How it works</p>
        {STEPS(days).map(({ icon: Icon, title, text }, i) => (
          <div key={title} className="panel flex gap-4 p-5 md:p-6" data-testid={`how-step-${i + 1}`}>
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-slate-800 text-blue-400"><Icon size={20} /></span>
            <div>
              <h3 className="font-display text-base font-semibold">{title}</h3>
              <p className="mt-1 text-sm leading-relaxed text-slate-400">{text}</p>
            </div>
          </div>
        ))}
        {prizes.length > 0 && (
          <div className="panel p-5 md:p-6" data-testid="landing-prizes">
            <p className="eyebrow mb-3 text-amber-400">Rewards · end of Day {days}</p>
            <ul className="space-y-2">
              {prizes.map((p) => (
                <li key={p.place} className="flex items-center gap-3 text-sm" data-testid={`landing-prize-${p.place}`}>
                  <span className="grid h-8 w-12 shrink-0 place-items-center rounded-lg bg-slate-800 font-mono text-xs font-bold text-amber-300">{ordinal(p.place)}</span>
                  <span className="text-slate-200">{p.reward}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>
      </div>
      <div className="md:hidden"><InstallPrompt /></div>
      <ContactLinks contact={season?.contact} />
      <p className="mt-6 text-center text-xs text-slate-600">Free to enter · Singapore beta · Schedule in SGT</p>
    </Shell>
  );
}
